// eslint-disable-next-line

import { notification } from "antd";
import jwt_decode from "jwt-decode";
import { createContext, useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import BASE_URL from "../constants";
import { decodeUser, readStoredAuthTokens } from "../utils/authTokenStorage";

const AuthContext = createContext();

export default AuthContext;

export const AuthProvider = ({ children }) => {
	const [authTokens, setAuthTokens] = useState(() => readStoredAuthTokens());
	const [user, setUser] = useState(() => decodeUser(authTokens));
	const [loading, setLoading] = useState(true);
	// Issue #75: 初期パスワードのままのユーザーへログイン後に警告モーダルを表示する
	const [showInitialPasswordWarning, setShowInitialPasswordWarning] =
		useState(false);

	// Issue #78 Codex指摘: HomePage初期表示のように複数APIが同時に期限切れを検知すると、
	// useAxios の各インターセプターが共有refreshの401失敗後にそれぞれ独立して
	// logoutUser({ sessionExpired: true }) を呼ぶ。通知はログアウト処理側で一度だけ
	// 出すため、同じセッション切れに対する2回目以降の呼び出しでは通知を抑止する。
	// 次回ログイン成功時にリセットし、次のセッション切れでは再び通知できるようにする。
	const sessionExpiredNotifiedRef = useRef(false);

	// Issue #184 Codexレビュー指摘: useAxios は axios インスタンス生成時点の
	// logoutUser をクロージャに握るため、別タブのログインで利用者が入れ替わった後に
	// 前の利用者の 401 が届くと、logoutUser の中の user は「入れ替わる前の利用者」に
	// なる。そのまま保存値と突き合わせると常に不一致となり、storage イベントで
	// 同期済みの新しい利用者の state まで破棄して /login へ飛ばしてしまう
	// （localStorage には有効なトークンが残るため、再読み込みするまでログアウト
	// したように見える）。判定には常に最新の user を使う。
	const userRef = useRef(user);
	// 判定に使う ref の更新を useEffect に委ねると、state を更新してから
	// effect が走るまでの間は ref が古い利用者を指す。その間に前の利用者の
	// 401 が届くと、同期済みの新しいセッションを破棄してしまう（別タブで
	// ログインした直後に再現する）。利用者の差し替えは必ずこの関数を通し、
	// state と ref を同じターンで揃える。
	const applyUser = useCallback((next) => {
		userRef.current = next;
		setUser(next);
	}, []);

	const navigate = useNavigate();

	const loginUser = async (e) => {
		e.preventDefault();
		const response = await fetch(`${BASE_URL}/api/token/`, {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify({
				username: e.target.username.value,
				password: e.target.password.value,
			}),
		});

		const data = await response.json();

		if (response.status === 200) {
			const userResponse = await fetch(`${BASE_URL}/user/get_user/`, {
				method: "GET",
				headers: {
					Authorization: `Bearer ${data.access}`,
				},
			});

			const userData = await userResponse.json();
			if (userData.isDeleted) {
				// Display an error notification for deleted user
				notification.error({
					message: "Login Failed",
					description:
						"Your account has been deleted. Please contact the administrator for assistance.",
				});
			} else {
				// Continue with the login process
				setAuthTokens(data);
				applyUser(jwt_decode(data.access));
				localStorage.setItem("authTokens", JSON.stringify(data));
				// Issue #78: 新しいセッションが始まるので、次にセッションが切れたときは
				// 再度通知できるようにガードをリセットする。
				sessionExpiredNotifiedRef.current = false;
				// Issue #75: 初期パスワードのまま（needsPasswordReset=True）なら警告モーダルを出す
				setShowInitialPasswordWarning(Boolean(userData.needsPasswordReset));
				navigate("/");
				// Display a success notification
				notification.success({
					message: "Login Successful",
					description: "You have successfully logged in.",
				});
			}
		} else {
			// Display an error notification for invalid credentials
			notification.error({
				message: "Login Failed",
				description: "Invalid username or password. Please try again.",
			});
		}
	};

	const logoutUser = (options = {}) => {
		const isSessionExpired = options.sessionExpired === true;

		// Issue #88 Codex指摘: 本番は ROTATE_REFRESH_TOKENS=True のため、2 つのタブが
		// 同時にトークン更新を行うと片方が 401 になりここへ来る。
		const storedRaw = localStorage.getItem("authTokens");
		let storedTokens = null;
		try {
			storedTokens = storedRaw ? JSON.parse(storedRaw) : null;
		} catch {
			storedTokens = null;
		}
		const storedAccess =
			typeof storedTokens?.access === "string" ? storedTokens.access : null;
		const storedRefresh =
			typeof storedTokens?.refresh === "string" ? storedTokens.refresh : null;

		// 判定は推測でなく「実際に 401 になった refresh トークン」を基準にする。
		// useAxios は保存済みの最新トークンを送ることがあるため、画面が持つ authTokens
		// との比較では、送ったトークン自身が失効した本物のセッション切れまで
		// 「別タブが更新した」と誤判定してしまう。
		// 保存値が失敗したトークンと別物 = 別タブが先に更新できた、と確定できる。
		const failedRefreshToken =
			typeof options.failedRefreshToken === "string"
				? options.failedRefreshToken
				: null;
		const anotherTabRefreshed =
			isSessionExpired &&
			failedRefreshToken !== null &&
			storedRefresh !== null &&
			storedRefresh !== failedRefreshToken;

		// (1) 取り残された古いリクエストの 401 は、セッション切れではなく同時更新の
		// 副産物。保存済みが同じ利用者のものなら、ログアウトせずそちらを採用して
		// 継続する（ここで state を捨てると、保存済みは有効なのに画面だけログアウトし、
		// 再読み込みで急にログイン済みに戻る）。
		if (anotherTabRefreshed) {
			const decoded = decodeUser(storedTokens);
			// user ではなく userRef を見る（上のコメント参照）。切替後は最新の
			// 利用者と保存値が一致するため、ここで継続でき破棄まで進まない。
			if (decoded && decoded.user_id === userRef.current?.user_id) {
				setAuthTokens(storedTokens);
				applyUser(decoded);
				sessionExpiredNotifiedRef.current = false;
				return;
			}
		}

		setAuthTokens(null);
		applyUser(null);
		setShowInitialPasswordWarning(false);
		// (2) 別タブのセッションを巻き添えで消さない。保存値がこのタブのものだと
		// 確認できたときだけ削除する（手動ログアウトでは両者が一致する）。
		// 壊れて読めない値はそのまま掃除する。
		if (
			storedRaw !== null &&
			(storedAccess === null || storedAccess === authTokens?.access)
		) {
			localStorage.removeItem("authTokens");
		}
		navigate("/login");
		// Issue #78: リフレッシュトークン失効による自動ログアウトのときだけ通知する。
		// 手動ログアウト（Header の Logout クリック）は options 省略のため従来どおり通知なし。
		// Issue #78 Codex指摘: 同一セッション切れに対して複数の並行リクエストがそれぞれ
		// logoutUser({ sessionExpired: true }) を呼んでも、通知は一度だけに抑える。
		if (isSessionExpired && !sessionExpiredNotifiedRef.current) {
			sessionExpiredNotifiedRef.current = true;
			notification.info({
				message: "Session Expired",
				description: "Your session has expired. Please log in again.",
			});
		}
	};

	// Issue #76 Codexレビュー対応: この関数をレンダーのたびに作り直すと、
	// ログイン直後の authTokens 更新 → setUser のような、警告表示とは
	// 無関係な通常の再レンダーでも参照が変わり、InitialPasswordWarningModal
	// 側の useEffect が誤って再実行されてしまう（結果、antd の通知
	// クリーンアップが onClose 経由の dismiss を誤発火させる）。
	// useCallback で参照を安定化し、不要な再実行を防ぐ。
	const dismissInitialPasswordWarning = useCallback(() => {
		setShowInitialPasswordWarning(false);
	}, []);

	const contextData = {
		user: user,
		authTokens: authTokens,
		setAuthTokens: setAuthTokens,
		setUser: applyUser,
		loginUser: loginUser,
		logoutUser: logoutUser,
		showInitialPasswordWarning: showInitialPasswordWarning,
		dismissInitialPasswordWarning: dismissInitialPasswordWarning,
	};

	// Issue #88: 同じブラウザで複数のタブを開いて使うため、別タブでログイン /
	// ログアウトしてもこのタブのヘッダーは古い表示のまま残っていた。localStorage の
	// 変更は他タブへ storage イベントとして届くので、それを購読して認証状態を合わせる。
	// 自タブでの setItem / removeItem では storage イベントが発火しない仕様のため、
	// 通常のログイン・ログアウト処理と二重に動くことはない。
	useEffect(() => {
		const handleStorage = (event) => {
			// key === null は localStorage.clear() のとき（全キーが対象）
			if (event.key !== null && event.key !== "authTokens") return;

			const stored = readStoredAuthTokens();
			if (!stored) {
				// 別タブでログアウトされた。PrivateRoutes が /login へ送る
				setAuthTokens(null);
				applyUser(null);
				setShowInitialPasswordWarning(false);
				return;
			}

			// 別タブでログイン（あるいは別ユーザーへ切り替え）された
			const decoded = decodeUser(stored);
			if (!decoded) return;
			setAuthTokens(stored);
			applyUser(decoded);
			// Issue #184 Codexレビュー指摘: 利用者が入れ替わるため、前の利用者へ出して
			// いた初期パスワード警告は必ず消す（残すと次の利用者に別人向けの警告が出る）。
			// 切替先が初期パスワードかどうかは、needsPasswordReset が JWT のクレームに
			// 無いため storage イベントだけでは判定できない。ここでは警告を出さず消す
			// だけにする。別タブでログインした本人には、そのタブの loginUser が
			// /user/get_user/ の結果で既に警告を表示している。
			setShowInitialPasswordWarning(false);
			// Issue #88 Codex指摘: ここも loginUser と同じく新しいセッションの開始なので、
			// Issue #78 の通知ガードを戻す。戻さないと、このタブで一度セッション切れを
			// 通知した後に別タブで再ログインした場合、次のセッション切れが無言になる。
			sessionExpiredNotifiedRef.current = false;
		};

		window.addEventListener("storage", handleStorage);
		return () => window.removeEventListener("storage", handleStorage);
	}, [applyUser]);

	useEffect(() => {
		if (authTokens) {
			const decoded = decodeUser(authTokens);
			if (decoded) {
				applyUser(decoded);
			} else {
				setAuthTokens(null);
				applyUser(null);
				localStorage.removeItem("authTokens");
			}
		}
		setLoading(false);
	}, [applyUser, authTokens]);

	return (
		<AuthContext.Provider value={contextData}>
			{loading ? null : children}
		</AuthContext.Provider>
	);
};
